import { message } from 'antd';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PaginationMeta from '../../types/PaginationMeta';
import ContactPersonAPI, {
  DEFAULT_CONTACT_PERSON_PAGE_SIZE,
  normalizeContactPersonsResponse,
} from '../api/ContactPersonAPI';
import ContactPerson from '../types/ContactPerson';
import { getFullContactName } from '../utils/contactPerson';

type UseContactPersonOptionsParams = {
  enabled?: boolean;
  hydrateSelectedIds?: ContactPerson['id'][];
  pageSize?: number;
  selectedContactPersons?: (ContactPerson | null | undefined)[];
};

const getUniqueContactPersons = (contactPersons: ContactPerson[]) => {
  const byId = new Map<ContactPerson['id'], ContactPerson>();

  contactPersons.forEach((contactPerson) => {
    byId.set(contactPerson.id, contactPerson);
  });

  return Array.from(byId.values());
};

const getUniqueIds = (ids: ContactPerson['id'][] = []) =>
  Array.from(new Set(ids));

const contactPersonMatchesQuery = (
  contactPerson: ContactPerson,
  query: string,
) => {
  const lowerCaseQuery = query.toLowerCase();

  return (
    getFullContactName(contactPerson).toLowerCase().includes(lowerCaseQuery) ||
    contactPerson.email.toLowerCase().includes(lowerCaseQuery)
  );
};

export default function useContactPersonOptions({
  enabled = true,
  hydrateSelectedIds = [],
  pageSize = DEFAULT_CONTACT_PERSON_PAGE_SIZE,
  selectedContactPersons = [],
}: UseContactPersonOptionsParams = {}) {
  const { t } = useTranslation();
  const [loadedContactPersons, setLoadedContactPersons] = useState<
    ContactPerson[]
  >([]);
  const [hydratedContactPersons, setHydratedContactPersons] = useState<
    ContactPerson[]
  >([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(
    null,
  );
  const [currentQuery, setCurrentQuery] = useState('');
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [hydratingSelected, setHydratingSelected] = useState(false);
  const debounceTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const pageRequestId = useRef(0);
  const hydrateRequestId = useRef(0);

  const selectedOptions = useMemo(
    () =>
      selectedContactPersons.filter(
        (contactPerson): contactPerson is ContactPerson => !!contactPerson,
      ),
    [selectedContactPersons],
  );

  const contactPersons = useMemo(
    () =>
      getUniqueContactPersons([
        ...selectedOptions,
        ...hydratedContactPersons,
        ...loadedContactPersons,
      ]),
    [hydratedContactPersons, loadedContactPersons, selectedOptions],
  );

  const selectedIdKey = useMemo(
    () =>
      getUniqueIds(hydrateSelectedIds)
        .sort((left, right) => left - right)
        .join(','),
    [hydrateSelectedIds],
  );

  const reportError = useCallback(
    (error: unknown) => {
      if (isAxiosError(error)) {
        message.error(
          error.response?.data.message || t('errors.somethingWentWrong'),
        );
        return;
      }

      message.error(t('errors.somethingWentWrong'));
    },
    [t],
  );

  const loadContactPersons = useCallback(
    async ({
      append = false,
      page = 1,
      query = currentQuery,
    }: {
      append?: boolean;
      page?: number;
      query?: string;
    } = {}) => {
      if (!enabled) {
        return;
      }

      const requestId = pageRequestId.current + 1;
      pageRequestId.current = requestId;
      const pagination = { page, pageSize };

      if (append) {
        setLoadingNextPage(true);
      } else {
        setLoadingNextPage(false);
        setLoadingPage(true);
      }

      try {
        const { data } = await ContactPersonAPI.getContactPersons(
          query ? { query } : undefined,
          pagination,
        );
        const response = normalizeContactPersonsResponse(data, pagination);
        const nextContactPersons = response.isPaginated
          ? response.contactPersons
          : response.contactPersons.filter((contactPerson) =>
              contactPersonMatchesQuery(contactPerson, query),
            );

        if (pageRequestId.current !== requestId) {
          return;
        }

        setCurrentQuery(query);
        setPaginationMeta(response.meta);
        setLoadedContactPersons((currentContactPersons) =>
          append
            ? getUniqueContactPersons([
                ...currentContactPersons,
                ...nextContactPersons,
              ])
            : nextContactPersons,
        );
      } catch (error) {
        if (pageRequestId.current === requestId) {
          reportError(error);
        }
      } finally {
        if (pageRequestId.current === requestId) {
          if (append) {
            setLoadingNextPage(false);
          } else {
            setLoadingPage(false);
          }
        }
      }
    },
    [currentQuery, enabled, pageSize, reportError],
  );

  const loadFirstPage = useCallback(
    (query = currentQuery) => {
      return loadContactPersons({ page: 1, query });
    },
    [currentQuery, loadContactPersons],
  );

  const searchContactPersons = useCallback(
    (query: string) => {
      if (debounceTimeoutId.current) {
        clearTimeout(debounceTimeoutId.current);
      }

      setCurrentQuery(query);
      setLoadedContactPersons([]);
      setPaginationMeta(null);
      pageRequestId.current += 1;
      setLoadingNextPage(false);
      setLoadingPage(true);

      debounceTimeoutId.current = setTimeout(() => {
        loadFirstPage(query);
      }, 500);
    },
    [loadFirstPage],
  );

  const hasMoreContactPersons =
    !!paginationMeta && paginationMeta.page < paginationMeta.lastPage;

  const loadMoreContactPersons = useCallback(() => {
    if (!hasMoreContactPersons || loadingNextPage || loadingPage) {
      return;
    }

    loadContactPersons({
      append: true,
      page: (paginationMeta?.page ?? 0) + 1,
    });
  }, [
    hasMoreContactPersons,
    loadContactPersons,
    loadingNextPage,
    loadingPage,
    paginationMeta?.page,
  ]);

  useEffect(() => {
    if (!enabled) {
      hydrateRequestId.current += 1;
      setHydratedContactPersons([]);
      setHydratingSelected(false);
      return;
    }

    const ids = selectedIdKey ? selectedIdKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      hydrateRequestId.current += 1;
      setHydratedContactPersons([]);
      setHydratingSelected(false);
      return;
    }

    const requestId = hydrateRequestId.current + 1;
    hydrateRequestId.current = requestId;

    const hydrateContactPersons = async () => {
      setHydratingSelected(true);
      const idSet = new Set(ids);
      let matchingContactPersons: ContactPerson[] = [];

      try {
        const pagination = {
          page: 1,
          pageSize: Math.max(ids.length, pageSize),
        };
        const { data } = await ContactPersonAPI.getContactPersons(
          { ids },
          pagination,
        );
        const response = normalizeContactPersonsResponse(data, pagination);
        matchingContactPersons = response.contactPersons.filter(
          (contactPerson) => idSet.has(contactPerson.id),
        );
      } catch {
        matchingContactPersons = [];
      }

      const foundIds = new Set(
        matchingContactPersons.map((contactPerson) => contactPerson.id),
      );
      const missingIds = ids.filter((id) => !foundIds.has(id));
      const individuallyLoaded = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const { data } = await ContactPersonAPI.getContactPerson(id);
            return data;
          } catch {
            return undefined;
          }
        }),
      );

      if (hydrateRequestId.current !== requestId) {
        return;
      }

      setHydratedContactPersons(
        getUniqueContactPersons([
          ...matchingContactPersons,
          ...individuallyLoaded.filter(
            (contactPerson): contactPerson is ContactPerson => !!contactPerson,
          ),
        ]),
      );
      setHydratingSelected(false);
    };

    hydrateContactPersons().catch((error) => {
      if (hydrateRequestId.current === requestId) {
        setHydratingSelected(false);
        reportError(error);
      }
    });
  }, [enabled, pageSize, reportError, selectedIdKey]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutId.current) {
        clearTimeout(debounceTimeoutId.current);
      }
    };
  }, []);

  return {
    contactPersons,
    hasMoreContactPersons,
    loadFirstContactPersonPage: loadFirstPage,
    loadMoreContactPersons,
    loadingContactPersons: loadingPage || hydratingSelected,
    loadingContactPersonOptions:
      loadingPage || loadingNextPage || hydratingSelected,
    searchContactPersons,
  };
}
